import { storeToRefs } from 'pinia';
import { usePermissionsStore } from '@/stores/permissions';
import { applyImpersonationOverlay } from '@/composables/impersonation';

/**
 * Composable for accessing user permissions in components.
 * Wraps the permissions store and provides reactive access to all permissions data.
 *
 * @example
 * ```vue
 * <script setup>
 * const { features, isAdmin, hasActionForPath } = usePermissions();
 * </script>
 *
 * <template>
 *   <div v-if="features.viewLiveStatus">Live Status</div>
 *   <div v-if="isAdmin">Admin Panel</div>
 *   <button v-if="hasActionForPath('u:driver')">Edit Driver</button>
 * </template>
 * ```
 */
export const usePermissions = () => {
  const store = usePermissionsStore();

  // storeToRefs preserves reactivity for all computed properties
  const {
    // Core permission checks
    isAdmin,
    isSuperAdmin,
    isSubContractor,
    isTenantAdmin,

    // Context state
    hasSingleContext,
    hasSingleTenantScope,
    isSingleTenantScope,
    contexts,

    // Features
    features,
    depotFeatures,
    tenantFeatures,

    // Paths
    depotPath,
    tenantPath,
    currentSelectedPath,

    // Selection state
    selectedContext,
    selectedTenant,
    selectedDepotPath,
    selectedDepotTenant,
    tenantConfiguration,

    // Tenants
    availableTenants,
    tenantContextTenants,
    tenantPermissions,
    tenantScopes,
    singleTenantScope,

    // Depot/Subcontractor
    subcontractorId,
    depotOrSuCo,
    depotPaths,
    depotActions
  } = storeToRefs(store);

  // Methods don't need storeToRefs (they're already functions)
  const {
    hasContext,
    hasTenantAccess,
    hasActionForPath,
    hasActionForTenantPath,
    isTenantAdminForPath,
    isTenantAdminForAnyPath,
    getActionsForPath,
    getAvailableRolesByContext,
    hasTenantInTenantActions,
    getTenantActionsForPath
  } = store;

  return applyImpersonationOverlay({
    // All reactive refs
    isAdmin,
    isSuperAdmin,
    isSubContractor,
    isTenantAdmin,
    hasSingleContext,
    hasSingleTenantScope,
    isSingleTenantScope,
    contexts,
    features,
    depotFeatures,
    tenantFeatures,
    depotPath,
    tenantPath,
    currentSelectedPath,
    selectedContext,
    selectedTenant,
    selectedDepotPath,
    selectedDepotTenant,
    availableTenants,
    tenantContextTenants,
    tenantPermissions,
    tenantScopes,
    singleTenantScope,
    subcontractorId,
    depotOrSuCo,
    tenantConfiguration,
    depotPaths,
    depotActions,

    // Methods
    hasContext,
    hasTenantAccess,
    hasActionForPath,
    hasActionForTenantPath,
    isTenantAdminForPath,
    isTenantAdminForAnyPath,
    getActionsForPath,
    getAvailableRolesByContext,
    hasTenantInTenantActions,
    getTenantActionsForPath
  });
};
